import {
    CloseCircleOutlined,
    CloseCircleTwoTone,
    MinusCircleTwoTone,
    PlusCircleTwoTone,
  } from "@ant-design/icons";
  import { Divider, Form } from "antd";
  import PrimaryButton from "components/Buttons/PrimaryButton/PrimaryButton";
  import TextInput from "components/Inputs/TextInput/TextInput";
  import Selector from "components/Selector/Selector";
  import useMessage from "hooks/useMessage";
  import { t } from "i18next";
  import PageLayout from "layout/PageLayout/PageLayout";
  import { FC, useState, useMemo } from "react";
  import { useDispatch, useSelector } from "react-redux";
  import { getToken } from "redux/features/app/authTokenSlice";
  import { setFeatureData } from "redux/features/app/featuresSettingSlice";
  import centralApi from "services/centralApi";
  import { API_ENDPOINTS } from "utils/constants";
  import { CampaignChannelTypeEnum, TimeUnitEnum } from "utils/enums";
  import "./RateLimit.css";
  
  const RateLimit: FC = () => {
    const loginUser = useSelector((state: any) => state.loginUser.data);
    const { appId } = useSelector((state: any) => state.activeApp);
    const rateLimit = useSelector(
      (state: any) => state.featuresSetting?.data[0]?.rateLimit
    );
    const appsData = useSelector((state: any) => state.featuresSetting?.data[0]);
    const channelStatus = appsData?.channel_status;
    const partnersData = appsData?.partnerInfo?.partnerData || [];
  
    const dispatch: any = useDispatch();
    const { showError, showSuccess } = useMessage();
  
    // Initialize rows
    const [rows, setRows] = useState(() =>
      Array.isArray(rateLimit) && rateLimit.length > 0
        ? rateLimit.map((r: any) => ({
            channel: r.channel || "",
            providers:
              Array.isArray(r.providers) && r.providers.length > 0
                ? r.providers.map((p: any) => ({
                    provider: p.provider || "",
                    request: p.request || "",
                    limit: p.limit || "",
                    timeWindow: p.timeWindow || "",
                    default: p.default ?? false, // 👈 added
                  }))
                : [
                    {
                      provider: "",
                      request: "",
                      limit: "",
                      timeWindow: "",
                      default: false,
                    },
                  ],
          }))
        : [
            {
              channel: "",
              providers: [
                { provider: "", request: "", limit: "", timeWindow: "" },
              ],
            },
          ]
    );
  
    // Available channels
    const channelOptions = useMemo(() => {
      const allChannels = Object.values(CampaignChannelTypeEnum);
      if (!channelStatus) {
        return allChannels.map((key) => ({ value: key, label: key }));
      }
      return allChannels
        .filter(
          (key) => key === CampaignChannelTypeEnum.ALL || channelStatus[key]
        )
        .map((key) => ({ value: key, label: key }));
    }, [channelStatus]);
  
    const timeWindowOptions = [
      { label: t("secondsLabel"), value: TimeUnitEnum.SECONDS },
      { label: t("minutesLabel"), value: TimeUnitEnum.MINUTES },
      { label: t("hoursLabel"), value: TimeUnitEnum.HOURS },
    ];
  
    // Handlers
    const handleChannelChange = (chIdx: number, value: string) => {
      setRows((prev) => {
        const updated = [...prev];
        updated[chIdx] = {
          ...updated[chIdx],
          channel: value,
          // Reset providers when channel changes
          providers: [{ provider: "", request: "", limit: "", timeWindow: "" }],
        };
        return updated;
      });
    };
  
    const handleProviderChange = (
      chIdx: number,
      provIdx: number,
      field: string,
      value: any
    ) => {
      setRows((prev) => {
        const updated = [...prev];
        updated[chIdx] = { ...updated[chIdx] };
        updated[chIdx].providers = [...updated[chIdx].providers];
        updated[chIdx].providers[provIdx] = {
          ...updated[chIdx].providers[provIdx],
          [field]: value,
        };
        return updated;
      });
    };
  
    const addProvider = (chIdx: number) => {
      setRows((prev) => {
        const updated = [...prev];
        updated[chIdx].providers.push({
          provider: "",
          request: "",
          limit: "",
          timeWindow: "",
        });
        return updated;
      });
    };
  
    const removeProvider = (chIdx: number, provIdx: number) => {
      setRows((prev) => {
        const updated = [...prev];
        updated[chIdx].providers = updated[chIdx].providers.filter(
          (_, i) => i !== provIdx
        );
        return updated;
      });
    };
  
    const addChannel = () => {
      setRows((prev) => [
        ...prev,
        {
          channel: "",
          providers: [{ provider: "", request: "", limit: "", timeWindow: "" }],
        },
      ]);
    };
  
    const removeChannel = (chIdx: number) => {
      setRows((prev) => prev.filter((_, i) => i !== chIdx));
    };
  
    const handleSubmit = async () => {
      try {
        const authToken = await dispatch(getToken()).unwrap();
        const payload = {
          args: { rateLimit: rows },
          app_id: appId,
          api_key: loginUser.api_key,
          authToken,
        };
        await centralApi("POST", API_ENDPOINTS.SAVE_APP_SETTING_URL, payload);
  
        dispatch(setFeatureData({ rateLimits: rows }));
        showSuccess(t("sucessfullySavedLabel"));
      } catch (error) {
        showError(t("somethingWrongLabel"));
      }
    };
  
    return (
      <PageLayout>
        <div className="rate-limit-main-container">
        <div className="mb-4">
          <h6 className="file-encryption-setting-container-heading">
            {t("rateLimitLabel")}
          </h6>
          <Divider className="file-encryption-setting-custom-divider" />
        </div>
  
        <Form
          className="w-100"
          onFinish={handleSubmit}
          layout="horizontal"
          labelCol={{ flex: "80px" }}
          labelAlign="left"
        >
          {rows.map((row, chIdx) => {
            const partnerOptions = partnersData
              .filter((p: any) => p.channel === row.channel)
              .map((p: any) => ({ value: p.partnerId, label: p.partnerName }));
  
            return (
              <div
                key={chIdx}
                className="mb-4 p-2 border rounded position-relative"
              >
                {rows.length > 1 && (
                  <PrimaryButton
                    onClick={() => removeChannel(chIdx)}
                    className="rate-limit-close-btn"
                  >
                    <CloseCircleTwoTone />
                  </PrimaryButton>
                )}
                <div className="d-flex align-items-center gap-2 mb-2">
                  <Selector
                    placeholder="Select channel"
                    className="rate-limit-selector"
                    value={row.channel}
                    onChange={(val) => handleChannelChange(chIdx, val)}
                    options={channelOptions}
                  />
                </div>
  
                {row.providers.map((prov, provIdx) => (
                  <div
                    key={provIdx}
                    className="d-flex gap-2 align-items-center mb-2"
                  >
                    <Selector
                      placeholder={t("selectProviderLabel")}
                      className="rate-limit-selector"
                      value={prov.provider}
                      onChange={(val) => {
                        handleProviderChange(chIdx, provIdx, "provider", val);
                        const providerInfo = (partnersData || []).find(
                          (p: any) => p.partnerId === val
                        );
                        handleProviderChange(
                          chIdx,
                          provIdx,
                          "default",
                          providerInfo?.default ?? false
                        );
                      }}
                      // onChange={(val) => handleProviderChange(chIdx, provIdx, "provider", val)}
                      options={partnerOptions}
                      disabled={!row.channel}
                    />
                    <TextInput
                      placeholder={t("numberOfrequestLabel")}
                      type="numeric"
                      className="rate-limit-selector"
                      value={prov.request}
                      onChange={(e) =>
                        handleProviderChange(
                          chIdx,
                          provIdx,
                          "request",
                          e.target.value
                        )
                      }
                    />
                    <TextInput
                      placeholder={t("limitLabel")}
                      type="numeric"
                      className="rate-limit-input"
                      value={prov.limit}
                      onChange={(e) =>
                        handleProviderChange(
                          chIdx,
                          provIdx,
                          "limit",
                          e.target.value
                        )
                      }
                    />
                    <Selector
                      placeholder={t("selectTimeLabel")}
                      className="rate-limit-selector"
                      value={prov.timeWindow}
                      onChange={(val) =>
                        handleProviderChange(chIdx, provIdx, "timeWindow", val)
                      }
                      options={timeWindowOptions}
                    />
  
                    {row.providers.length > 1 && (
                      <PrimaryButton
                        onClick={() => removeProvider(chIdx, provIdx)}
                      >
                        <MinusCircleTwoTone />
                      </PrimaryButton>
                    )}
  
                    {provIdx === row.providers.length - 1 && (
                      <PrimaryButton onClick={() => addProvider(chIdx)}>
                        <PlusCircleTwoTone />
                      </PrimaryButton>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
  
          <div className="d-flex gap-2">
            <PrimaryButton onClick={addChannel}>
              {t("addChannelLabel")}
            </PrimaryButton>
            <PrimaryButton type="primary" htmlType="submit">
              {t("saveLabel")}
            </PrimaryButton>
          </div>
        </Form>
        </div>
      </PageLayout>
    );
  };
  
  export default RateLimit;
  